import {
  BaseClass,
  INode,
  INodeSizeConstraintProvider,
  Rect,
  Size,
} from '@ardoq/yfiles';
import {
  CollapsibleGraphGroup,
  estimateCountLabelWidth,
  MODERNIZED_BLOCK_DIAGRAM_BADGE_HEIGHT as BADGE_HEIGHT,
} from '@ardoq/graph';
import {
  measureNodeLabel,
  MODERNIZED_BLOCK_DIAGRAM_LINE_HEIGHT as LINE_HEIGHT,
} from '@ardoq/graph-labeling';
import {
  MODERNIZED_BLOCK_DIAGRAM_COLLAPSED_GROUP_HEIGHT as COLLAPSED_GROUP_HEIGHT,
  MODERNIZED_BLOCK_DIAGRAM_EXPANDER_SIZE as EXPANDER_SIZE,
  MODERNIZED_BLOCK_DIAGRAM_GROUP_PADDING as PADDING,
} from './consts';
import {
  MODERNIZED_BLOCK_DIAGRAM_NODE_WIDTH as NODE_WIDTH,
  MODERNIZED_BLOCK_DIAGRAM_EXPANDER_MARGIN_RIGHT as EXPANDER_MARGIN_RIGHT,
  MODERNIZED_BLOCK_DIAGRAM_COLLAPSED_COMPONENT_GROUP_HEIGHT as COLLAPSED_COMPONENT_GROUP_HEIGHT,
} from 'yfilesExtensions/styles/modernized/consts';
import { otherLabelsHeight } from './leafNodeLabel/otherLabelsHeight';
import { wrapString } from 'tabview/blocks/misc/string';

const PREFERRED_MINIMUM_WIDTH = 340;

class GroupSizeConstraintProvider extends BaseClass(
  INodeSizeConstraintProvider
) {
  constructor(private groupNode: INode) {
    super();
  }
  override getMaximumSize(): Size {
    return Size.INFINITE;
  }
  override getMinimumEnclosedArea(): Rect {
    return Rect.EMPTY;
  }
  override getMinimumSize(): Size {
    const graphGroup = this.groupNode.tag as CollapsibleGraphGroup | null;
    if (!graphGroup) {
      return Size.EMPTY;
    }
    const { collapsed, descendantCount } = graphGroup;
    if (collapsed) {
      const isComponent = graphGroup.isComponent();
      if (isComponent) {
        // a collapsed component group looks like a leaf node.
        return new Size(
          NODE_WIDTH,
          COLLAPSED_COMPONENT_GROUP_HEIGHT + otherLabelsHeight(graphGroup)
        );
      }
      // a collapsed non-component group is just a rectangle with an expander, main label, and descendant count badge.
      const mainLabel = graphGroup.getItemLabels()?.mainLabel ?? '';
      const wrappedMainLabel = wrapString(
        mainLabel,
        PREFERRED_MINIMUM_WIDTH,
        measureNodeLabel
      );
      const labelWidth =
        wrappedMainLabel.length === 1
          ? measureNodeLabel(mainLabel)
          : PREFERRED_MINIMUM_WIDTH;
      const descendantCountBadgeWidth =
        estimateCountLabelWidth(descendantCount) + BADGE_HEIGHT;
      const minimumWidth =
        PADDING +
        EXPANDER_SIZE +
        EXPANDER_MARGIN_RIGHT +
        labelWidth +
        PADDING +
        descendantCountBadgeWidth +
        PADDING;
      const labelHeight = wrappedMainLabel.length * LINE_HEIGHT;
      const stripeContentHeight = Math.max(labelHeight, BADGE_HEIGHT);
      const minimumHeight = stripeContentHeight + 2 * PADDING;
      return new Size(minimumWidth, minimumHeight);
    }
    // an open group will mainly be sized by its content. GroupNodeInsetsDataProvider will handle the group size after the content has been measured.
    return new Size(1, COLLAPSED_GROUP_HEIGHT);
  }
}
export { GroupSizeConstraintProvider };
